import React from "react";
import { ChevronRight, FileText, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { JSONObject } from "@/components/jsonSchemaForm/types";

interface IndexSelectorProps {
  items: JSONObject[];
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  confirmedParts?: Set<number>;
}

/**
 * IndexSelector component displays a collapsible sidebar of items.
 * The sidebar defaults to collapsed and expands on hover.
 * All items are displayed with a red file icon.
 */
export const IndexSelector: React.FC<IndexSelectorProps> = ({
  items,
  selectedIndex,
  onSelectIndex,
  confirmedParts = new Set(),
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const getItemMetadata = (item: JSONObject) => {
    const partNumber = item.partNumber;
    const partDescription = item.partDescription;
    const type = item.type;

    return {
      partNumber: typeof partNumber === "string" ? partNumber : undefined,
      partDescription:
        typeof partDescription === "string" ? partDescription : undefined,
      type: typeof type === "string" ? type : undefined,
    };
  };

  return (
    <div
      className={cn(
        "flex flex-col h-full border-r bg-muted/5 overflow-hidden transition-all duration-300 ease-in-out",
        isExpanded ? "w-64" : "w-12",
      )}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {!isExpanded && (
        <div className="p-4 w-full flex justify-center">
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      {isExpanded && (
        <div className="flex-1 overflow-auto">
          <div className="p-2 space-y-1">
            {items.map((item, index) => {
              const metadata = getItemMetadata(item);
              const isSelected = index === selectedIndex;
              const isConfirmed = confirmedParts.has(index);

              return (
                <button
                  key={index}
                  onClick={() => onSelectIndex(index)}
                  className={cn(
                    "w-full text-left p-3 rounded-md transition-colors",
                    "border hover:bg-accent/50 focus:outline-none overflow-hidden",
                    isSelected
                      ? "bg-accent border-accent-foreground/20"
                      : "bg-background border-transparent",
                  )}
                >
                  <div className="flex items-start gap-2 overflow-hidden">
                    <div className="flex-shrink-0 mt-0.5">
                      <FileText className="h-4 w-4 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          Part {index + 1}
                        </span>
                        {isConfirmed && (
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        )}
                      </div>
                      {metadata.partNumber && (
                        <div className="text-xs text-muted-foreground truncate">
                          {metadata.partNumber}
                        </div>
                      )}
                      {metadata.type && (
                        <div className="text-xs text-muted-foreground truncate">
                          {metadata.type}
                        </div>
                      )}
                      {metadata.partDescription && (
                        <div className="text-xs text-muted-foreground truncate mt-0.5">
                          {metadata.partDescription}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
